import React, { ComponentProps, PropsWithChildren, useCallback, useImperativeHandle, useRef, useState } from 'react'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import styled from 'styled-components'
import IconButton from 'components/Luxkit/Button/IconButton'
import LineAngleLeftIcon from 'components/Luxkit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'components/Luxkit/Icons/line/LineAngleRightIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import cn from 'clsx'
import { MIN_MONTH_TAB_BUTTON_WIDTH } from './MonthTabButton'

const SCROLL_ADVANCE = MIN_MONTH_TAB_BUTTON_WIDTH * 3

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`

const Container = styled(HorizontalContainerWithFadedOverflow)`
  display: flex;
  gap: ${rem(8)};
  flex-direction: row;
  align-items: stretch;
`

const FloatingActionContainer = styled.div`
  display: none;
  position: absolute;
  top: 50%;
  pointer-events: none;
  opacity: 1;
  transition: .2s ease opacity;

  ${mediaQueryUp.sm} {
    display: block;
  }

  > * {
    pointer-events: initial;
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }

  &.at-start {
    left: 0;
    transform: translateX(-50%) translateY(-50%);
  }

  &.at-end {
    right: 0;
    transform: translateX(50%) translateY(-50%);
  }

  &.is-hidden {
    opacity: 0;

    > * {
      pointer-events: none;
    }
  }
`

interface Props {
  className?: string
}

const MonthTabButtonsGroup = React.forwardRef<HTMLDivElement, PropsWithChildren<Props>>((props, ref) => {
  const {
    children,
    className,
  } = props

  const tabsGroupRef = useRef<HTMLDivElement>()

  useImperativeHandle(ref, () => tabsGroupRef.current)

  const scrollBackward = useCallback(() => {
    tabsGroupRef.current.scrollLeft -= SCROLL_ADVANCE + (tabsGroupRef.current.scrollLeft % SCROLL_ADVANCE)
  }, [])

  const scrollForward = useCallback(() => {
    tabsGroupRef.current.scrollLeft += SCROLL_ADVANCE + (tabsGroupRef.current.scrollLeft % SCROLL_ADVANCE)
  }, [])

  const [hasReachedStart, setHasReachedStart] = useState<boolean>(false)
  const [hasReachedEnd, setHasReachedEnd] = useState<boolean>(false)

  const handleReachStart = useCallback<ComponentProps<typeof HorizontalContainerWithFadedOverflow>['onHasReachedStart']>((hasReached) => {
    setHasReachedStart(hasReached)
  }, [setHasReachedStart])
  const handleReachEnd = useCallback<ComponentProps<typeof HorizontalContainerWithFadedOverflow>['onHasReachedStart']>((hasReached) => {
    setHasReachedEnd(hasReached)
  }, [setHasReachedEnd])

  return <Wrapper className={className}>
    <Container
      ref={tabsGroupRef}
      onHasReachedStart={handleReachStart}
      onHasReachedEnd={handleReachEnd}
    >
      {children}
    </Container>
    <FloatingActionContainer
      className={cn('at-start', {
        'is-hidden': hasReachedStart,
      })}
    >
      <IconButton
        kind="primary"
        variant="ghost"
        size="small"
        shape="circle"
        onClick={scrollBackward}
      >
        <LineAngleLeftIcon />
      </IconButton>
    </FloatingActionContainer>
    <FloatingActionContainer
      className={cn('at-end', {
        'is-hidden': hasReachedEnd,
      })}
    >
      <IconButton
        kind="primary"
        size="small"
        variant="ghost"
        shape="circle"
        onClick={scrollForward}
      >
        <LineAngleRightIcon />
      </IconButton>
    </FloatingActionContainer>
  </Wrapper>
})

export default MonthTabButtonsGroup
